import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Typography, List, Button, Empty, message, Drawer, Grid, Popconfirm } from 'antd';
import { UserOutlined, HistoryOutlined, PlusOutlined, LogoutOutlined, DeleteOutlined, MenuOutlined } from '@ant-design/icons';
import LoginPage from './components/LoginPage';
import BaziPanel from './components/BaziPanel';
import './App.css';

const { Sider, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

function App() {
  const [username, setUsername] = useState(localStorage.getItem('bazi_user') || '');
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [draftKey, setDraftKey] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const usernameRef = useRef(username);
  const recordsRequestRef = useRef({ generation: 0, controller: null });
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const fetchRecords = useCallback(async (user) => {
    const generation = recordsRequestRef.current.generation + 1;
    recordsRequestRef.current.controller?.abort();
    const controller = new AbortController();
    recordsRequestRef.current = { generation, controller };
    try {
      const res = await fetch(`/api/user/records?username=${encodeURIComponent(user)}`, {
        signal: controller.signal
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '加载历史记录失败');
      if (recordsRequestRef.current.generation === generation && usernameRef.current === user) {
        setRecords(data.records || []);
      }
    } catch (error) {
      if (
        error.name !== 'AbortError' &&
        recordsRequestRef.current.generation === generation &&
        usernameRef.current === user
      ) {
        message.error(error.message || '加载历史记录失败');
      }
    }
  }, []);

  useEffect(() => {
    if (username) {
      fetchRecords(username);
    } else {
      recordsRequestRef.current.controller?.abort();
    }
    return () => recordsRequestRef.current.controller?.abort();
  }, [username, fetchRecords]);

  const handleLogin = (user) => {
    usernameRef.current = user;
    setUsername(user);
    localStorage.setItem('bazi_user', user);
  };

  const handleLogout = () => {
    usernameRef.current = '';
    recordsRequestRef.current.controller?.abort();
    recordsRequestRef.current = {
      generation: recordsRequestRef.current.generation + 1,
      controller: null
    };
    setUsername('');
    localStorage.removeItem('bazi_user');
    setRecords([]);
    setSelectedRecord(null);
  };

  const handleDeleteRecord = async (recordId, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`/api/user/records/${recordId}?username=${encodeURIComponent(username)}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '删除失败');
      message.success('已删除');
      fetchRecords(username);
      if (selectedRecord && selectedRecord.id === recordId) {
        setSelectedRecord(null);
        setDraftKey(key => key + 1);
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  const handleSelectRecord = (item) => {
    setSelectedRecord(item);
    if (isMobile) setDrawerOpen(false);
  };

  if (!username) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const sidebarContent = (
    <>
      <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text strong><UserOutlined /> {username}</Text>
        <Button type="text" size="small" icon={<LogoutOutlined />} onClick={handleLogout} aria-label="退出登录" />
      </div>
      <div style={{ padding: '12px 16px' }}>
        <Button type="primary" block icon={<PlusOutlined />} onClick={() => {
          setSelectedRecord(null);
          setDraftKey(key => key + 1);
          if (isMobile) setDrawerOpen(false);
        }}>
          新排盘
        </Button>
      </div>
      <div style={{ padding: '0 8px' }}>
        <Text type="secondary" style={{ padding: '0 8px', fontSize: 12 }}>
          <HistoryOutlined /> 历史记录
        </Text>
        {records.length === 0 ? (
          <Empty description="暂无记录" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 20 }} />
        ) : (
          <List size="small" dataSource={records} style={{ marginTop: 8 }}
            renderItem={item => (
              <List.Item
                style={{ cursor: 'pointer', padding: '8px 12px', borderRadius: 6,
                  background: selectedRecord?.id === item.id ? '#e6f7ff' : 'transparent' }}
                onClick={() => handleSelectRecord(item)}
                extra={(
                  <Popconfirm
                    title="删除这条排盘记录？"
                    description="相关分析也会一并删除，此操作无法撤销。"
                    okText="删除"
                    cancelText="取消"
                    onConfirm={(e) => handleDeleteRecord(item.id, e)}
                    onCancel={(e) => e?.stopPropagation()}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={`删除${item.name || '排盘'}记录`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                )}
              >
                <List.Item.Meta
                  title={<Text style={{ fontSize: 13 }}>{item.name}</Text>}
                  description={<Text type="secondary" style={{ fontSize: 11 }}>
                    {item.year}-{item.month}-{item.day} {item.gender === 'male' ? '男' : '女'} | {item.created_at}
                  </Text>}
                />
              </List.Item>
            )} />
        )}
      </div>
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 桌面端侧边栏 */}
      {!isMobile && (
        <Sider width={260} theme="light" style={{ borderRight: '1px solid #f0f0f0' }}>
          {sidebarContent}
        </Sider>
      )}
      {/* 移动端抽屉 */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={280}
          styles={{ body: { padding: 0 } }}
          title="八字排盘"
        >
          {sidebarContent}
        </Drawer>
      )}
      <Layout>
        {/* 移动端顶部栏 */}
        {isMobile && (
          <div style={{ padding: '12px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Button type="text" icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} aria-label="打开历史记录" />
            <Text strong style={{ fontSize: 16 }}>八字排盘</Text>
            <div style={{ width: 32 }} />
          </div>
        )}
        <Content style={{ background: '#f5f5f5', overflow: 'auto' }}>
          <BaziPanel username={username} key={`${selectedRecord?.id || 'new'}:${draftKey}`} initialRecord={selectedRecord}
            onRecordSaved={() => fetchRecords(username)} />
        </Content>
      </Layout>
    </Layout>
  );
}

export default App;
